import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Lock, KeyRound, BookOpen, Sparkles, Palette, Ear, Type,
  Hand, Users, Baby, GraduationCap, Briefcase, Zap, DollarSign,
  TrendingUp, Rocket, Target, Building2, PieChart, Wallet, Check,
  AlertTriangle, PenTool, Cpu, Music, Wand2, Volume2, Download,
} from "lucide-react";
import { exportPitchPptx } from "@/lib/exportPitchPptx";

const OWL_PPTX_SLIDES = [
  { kicker: "// 01 · COVER & VISION", title: "Reimagining reading for the next generation of learners.", body: "Sleepy Owl + Dream CapCha is a multi-sensory interactive publishing platform built for every learning style. Static text hasn't evolved in centuries — Sleepy Owl transforms reading into an active, multi-modal experience.", bullets: ["85% functional MVP live", "$28B+ TAM · Digital + EdTech", "$7,000 fixed MVP build cost", "VARK-native from Day 1"] },
  { kicker: "// 02 · THE LITERACY & ENGAGEMENT GAP", title: "The reading experience hasn't evolved — but readers have.", body: "Traditional print + standard e-readers cater almost exclusively to read/write learners, leaving visual, auditory, and kinesthetic learners behind — while kids growing up on TikTok-first media have zero patience for static pages.", bullets: ["Literacy Crisis · 3 of 4 learning styles unserved", "Engagement Friction · ~60m/day screen time · kids 8-12", "Author Limitations · $0 affordable interactive tools"] },
  { kicker: "// 03 · A MULTI-SENSORY PUBLISHING PLATFORM", title: "One Dream CapCha engine. Four learning styles. Infinite depth.", body: "Embedded digital media — audio soundscapes, AI artwork, primary sources, video loops — mapped to specific words and phrases. Built on the VARK model so every reader engages the way their brain works best.", bullets: ["Visual · AI artwork · infographics · video clips per word", "Auditory · text-synced narration · pronunciation · ambient", "Read/Write · OpenDyslexic · inline dictionaries · notes", "Kinesthetic · tap-to-reveal · interactive maps · choice nodes"] },
  { kicker: "// 04 · DREAM CAPCHA · PANELS TO LIFE", title: "Still panels → living scenes.", body: "One click inside the Author Studio and a static comic page becomes a fully animated sequence. Every source panel maps to a Veo-3 / Kling-generated video loop, embedded directly at the same scene anchor.", bullets: ["Panel upload · scene tagging · Veo-3 / Kling render · Reader embed", "3 native video loops generated per comic page", "Zero manual editing — full auto pipeline"] },
  { kicker: "// 05 · FEATURED TITLE · NEPHILIM", title: "One book. Cinematic scenes on demand.", body: "Nephilim by Lao — a fantasy manuscript that becomes an animated scene inside Dream CapCha. Real example, live on the platform today.", bullets: ["Ch.01 · The Awakening — celestial sparks", "Ch.04 · The Protector's Line — reality bends", "Ch.09 · The Celestial Court — Metatron's grief", "Genre tags: Fantasy · Mythic · Celestial · Coming-of-Age"] },
  { kicker: "// 06 · AGE-ADAPTIVE UX + OPEN CATALOG", title: "Universal accessibility. One reader. Four adaptive layers.", body: "Device-agnostic HTML5 web app — zero app-store friction. Unrestricted catalog access · UI dynamically adapts to reader age band.", bullets: ["3–7 · Early Readers · big touch targets · phonics audio", "8–12 · Middle Grade · illustrated · atmospheric audio", "13–17 · Young Adult · cinematic soundscapes · worldmaps", "18+ · Adult · primary-source popups · professional narration", "Real-time switching: font · vocab · media depth · audio · density"] },
  { kicker: "// 07 · PRODUCT READINESS · THE 85% MVP", title: "Live and revenue-ready. Not vaporware.", body: "A functional MVP is already live with the three core systems shipping. Capital efficiency: built for rapid deployment so we can onboard authors and generate revenue immediately.", bullets: ["HTML5 Universal Reader — device-agnostic · VARK controls", "Dream CapCha Author Studio — OpenAI + ElevenLabs pipelines", "Built-in Monetization — Stripe pre-orders live at MVP", "85% shipped · 15% remaining"] },
  { kicker: "// 08 · DUAL-ENGINE MONETIZATION", title: "Two revenue engines. Creator value. Consumer value.", body: "Monetization is built directly into the platform to capture value on both sides of the marketplace simultaneously.", bullets: ["B2C · Individual title sales · pre-order campaigns · family passes", "B2B · Author Studio SaaS · EdTech licensing · white-label integrations"] },
  { kicker: "// 09 · MARKET OPPORTUNITY", title: "Tapping into a $28B+ market with a phased approach.", body: "Combined market across Digital Publishing, Children's Media, and EdTech Literacy — the three categories Sleepy Owl serves natively.", bullets: ["Phase 01 · Founding Authors + Pilots — revenue Day 1 post-close", "Phase 02 · Final 15% + Native Apps + Multi-Publisher scaling"] },
  { kicker: "// 10 · THE ASK · CAPITAL ALLOCATION", title: "Seeking seed / angel funding — or strategic partnership.", body: "The platform is live, the pipes are built, the AI stack is wired. Capital is fuel — not runway to figure out what to build.", bullets: ["40% · Marketing & Author Acquisition", "35% · Product Polish · The Final 15%", "25% · B2B Sales & Institutional Pilots", "Closing: \"Everyone's story deserves to be told.\""] },
];

const PIN = "2353";
const STORAGE_KEY = "sleepyowl_pitch_unlocked";
const V = "#7c3aed";   // Violet (dream/magic)
const A = "#f59e0b";   // Amber (owl/warm)

// ============ PIN LOCK ============
function PinLock({ onUnlock }) {
  const [entered, setEntered] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const handleChange = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...entered];
    next[i] = val;
    setEntered(next);
    setError(false);
    if (val && i < 3) document.getElementById(`owl-pin-${i + 1}`)?.focus();
    if (next.join("").length === 4) {
      if (next.join("") === PIN) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setTimeout(onUnlock, 300);
      } else {
        setError(true);
        setTimeout(() => setEntered(["", "", "", ""]), 400);
      }
    }
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !entered[i] && i > 0) {
      document.getElementById(`owl-pin-${i - 1}`)?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden" data-testid="owl-pin-lock">
      <div className="absolute inset-0 te-grid-bg opacity-25" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#7c3aed]/20 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#f59e0b]/15 blur-[140px]" />

      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full mx-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-[#7c3aed]/10 border border-[#7c3aed]/40 flex items-center justify-center">
            <Lock size={26} style={{ color: V }} />
          </div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: V }}>// CONFIDENTIAL · PIN REQUIRED</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter">Sleepy Owl · Investor Vault</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            8-slide investor deck for the Sleepy Owl + Dream CapCha next-generation publishing platform. Enter your access PIN.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          {entered.map((v, i) => (
            <input
              key={i}
              id={`owl-pin-${i}`}
              data-testid={`owl-pin-${i}`}
              value={v}
              onChange={(e) => handleChange(i, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              type="password"
              autoComplete="off"
              className={`h-16 w-14 text-center font-display text-3xl font-bold rounded-2xl bg-slate-950 border-2 outline-none transition-all ${error ? "border-red-500 animate-pulse" : v ? "border-[#7c3aed] shadow-[0_0_15px_-3px_rgba(124,58,237,0.5)]" : "border-slate-800 focus:border-[#7c3aed]/60"}`}
            />
          ))}
        </div>

        {error && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
            // ACCESS DENIED · INVALID PIN
          </motion.div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <KeyRound size={11} /> Session-locked · closes with your browser tab
        </div>
      </motion.div>
    </div>
  );
}

// ============ REUSABLE SLIDE FRAME ============
function SlideFrame({ num, kicker, children, extraBg }) {
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80 overflow-hidden" data-testid={`owl-slide-${num}`}>
      <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
      {extraBg}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] mb-4" style={{ color: V }}>{kicker}</div>
        </motion.div>
        {children}
      </div>
    </section>
  );
}

// ============ THE 8 SLIDES ============

function SlideCover() {
  return (
    <section className="relative overflow-hidden border-b border-slate-800/80" data-testid="owl-slide-1">
      <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.4}} className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#7c3aed]/20 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.8,delay:0.4}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#f59e0b]/15 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16 lg:pb-24 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div>
          <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="inline-flex items-center gap-2 border rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ borderColor: `${V}66`, background: `${V}0F`, color: V }}>
            <BookOpen size={12} /> Sleepy Owl + Dream CapCha · Investor Deck · 01 / 08
          </motion.div>
          <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9,delay:0.1,ease:[0.22,1,0.36,1]}} className="mt-8 font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[0.98] tracking-tighter">
            Reimagining <span style={{ color: V }}>reading</span> for the next generation of <span style={{ color: A }}>learners</span>.
          </motion.h1>
          <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.25}} className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Sleepy Owl + Dream CapCha is a <span style={{ color: V }} className="font-semibold">multi-sensory interactive publishing platform</span> built for every learning style. Static text hasn&apos;t evolved in centuries — Sleepy Owl transforms reading into an <span style={{ color: A }} className="font-semibold">active, multi-modal experience</span>.
          </motion.p>
          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { k: "85%", v: "Functional MVP", c: V },
              { k: "$28B+", v: "TAM · Digital / EdTech", c: A },
              { k: "$7,000", v: "Fixed MVP build", c: "#10b981" },
            ].map((s, i) => (
              <motion.div key={i} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.3+i*0.08}} className="border border-slate-800/80 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-4">
                <div className="font-display text-3xl font-bold tracking-tighter" style={{ color: s.c }}>{s.k}</div>
                <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">{s.v}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Book graphic — glowing Dream CapCha touchpoints */}
        <motion.div initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}} transition={{duration:1.2,delay:0.3}} className="relative">
          <div className="absolute -inset-6 rounded-3xl blur-3xl opacity-70" style={{ background: `radial-gradient(circle at 30% 20%, ${V}44, transparent 60%), radial-gradient(circle at 70% 80%, ${A}33, transparent 60%)` }} aria-hidden />
          <div className="relative bg-slate-900/70 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-6 lg:p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
            {/* Fake book chrome */}
            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mb-4">
              <span>◀ chapter 4</span>
              <span style={{ color: V }}>The Owl&apos;s Descent</span>
              <span>pg. 47</span>
            </div>
            <div className="space-y-3 text-[13px] leading-relaxed text-slate-300 font-serif">
              <p>
                As <span className="relative inline-block underline decoration-2 underline-offset-4" style={{ textDecorationColor: V, color: "#e0d4ff" }}>Elara<span className="absolute -top-2 -right-3 h-2 w-2 rounded-full animate-pulse" style={{ background: V, boxShadow: `0 0 10px ${V}` }} /></span> pressed her palm to the humming glass, a low tone gathered in the <span className="relative inline-block" style={{ color: "#fde3a5" }}>archive room<span className="absolute -top-2 -right-2 h-2 w-2 rounded-full animate-pulse" style={{ background: A, boxShadow: `0 0 10px ${A}` }} /></span> — a sound she had once heard in a dream she couldn&apos;t remember.
              </p>
              <p>
                Above her, the <span className="relative inline-block underline decoration-2 underline-offset-4" style={{ textDecorationColor: A, color: "#fde3a5" }}>constellation map<span className="absolute -top-2 -right-3 h-2 w-2 rounded-full animate-pulse" style={{ background: A, boxShadow: `0 0 10px ${A}` }} /></span> unfolded across the ceiling in silver ink, each star naming itself as her eye landed on it.
              </p>
            </div>
            {/* Interactive chips */}
            <div className="mt-6 grid grid-cols-4 gap-2">
              {[
                { Icon: Palette, l: "AI Art", c: V },
                { Icon: Volume2, l: "Soundscape", c: A },
                { Icon: BookOpen, l: "Primary Src", c: "#10b981" },
                { Icon: Hand, l: "Tap Reveal", c: V },
              ].map((chip, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5 py-2 rounded-lg" style={{ background: `${chip.c}0F`, border: `1px solid ${chip.c}44` }}>
                  <chip.Icon size={13} style={{ color: chip.c }} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: chip.c }}>{chip.l}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SlideProblem() {
  const problems = [
    { Icon: TrendingUp, title: "Literacy Crisis", desc: "Traditional print and standard e-readers cater almost exclusively to read/write learners — leaving visual, auditory, and kinesthetic learners behind.", stat: "3 of 4", statLabel: "learning styles unserved" },
    { Icon: AlertTriangle, title: "Engagement Friction", desc: "Digital-native kids grew up on rich, multi-sensory media. Static book pages simply can&apos;t compete for screen time in a TikTok-first attention economy.", stat: "~60m", statLabel: "avg. daily screen time · kids 8-12" },
    { Icon: PenTool, title: "Author Limitations", desc: "Independent authors and publishers lack accessible, low-cost tools to create native, interactive multi-media books — leaving huge creative surface untapped.", stat: "$0", statLabel: "affordable interactive publishing tools" },
  ];
  return (
    <SlideFrame num={2} kicker="// 02 · THE LITERACY & ENGAGEMENT GAP">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The reading experience <span style={{ color: V }}>hasn&apos;t evolved</span> — but readers <span style={{ color: A }}>have</span>.</h2>
      <div className="mt-10 grid md:grid-cols-3 gap-3">
        {problems.map((p, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.6,delay:i*0.08}} className="bg-slate-900/60 border border-red-900/40 backdrop-blur-xl rounded-2xl p-6">
            <div className="h-11 w-11 rounded-xl bg-red-950/50 border border-red-500/40 flex items-center justify-center"><p.Icon size={17} className="text-red-400" /></div>
            <h3 className="mt-4 font-display text-xl font-bold">{p.title}</h3>
            <p className="mt-2 text-[13px] text-slate-400 leading-relaxed" dangerouslySetInnerHTML={{__html: p.desc}} />
            <div className="mt-5 pt-4 border-t border-slate-800/60">
              <div className="font-display text-2xl font-bold text-red-400">{p.stat}</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">{p.statLabel}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </SlideFrame>
  );
}

function SlideSolution() {
  const vark = [
    { Icon: Palette, title: "Visual", desc: "AI-generated artwork, primary source photos, infographics, and looping video clips mapped to specific words.", c: V },
    { Icon: Ear, title: "Auditory", desc: "Text-synced narration, historical speeches, ambient soundscapes, and pronunciation guides.", c: A },
    { Icon: Type, title: "Read / Write", desc: "OpenDyslexic-friendly fonts, adjustable spacing, inline dictionaries, and personal margin notes.", c: "#10b981" },
    { Icon: Hand, title: "Kinesthetic", desc: "Interactive tap-to-reveal tags, drag-and-explore maps, and interactive choice nodes.", c: "#ec4899" },
  ];
  return (
    <SlideFrame num={3} kicker="// 03 · A MULTI-SENSORY PUBLISHING PLATFORM">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">One <span style={{ color: V }}>Dream CapCha engine</span>. Four learning styles. <span style={{ color: A }}>Infinite depth.</span></h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Embedded digital media — audio soundscapes, AI-generated artwork, primary sources, and video loops — directly mapped to specific words and phrases. Built around the <span style={{ color: V }} className="font-semibold">VARK Learning Model</span> so every reader engages the way their brain works best.</p>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {vark.map((v, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.6,delay:i*0.08}} className="rounded-2xl p-6 border-2 backdrop-blur-xl" style={{ borderColor: `${v.c}66`, background: `linear-gradient(135deg, ${v.c}12, rgba(15,23,42,0.6))` }}>
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `${v.c}1A`, border: `1px solid ${v.c}66` }}>
              <v.Icon size={20} style={{ color: v.c }} />
            </div>
            <div className="mt-4 font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: v.c }}>{v.title.charAt(0)}</div>
            <h3 className="mt-1 font-display text-2xl font-bold">{v.title}</h3>
            <p className="mt-3 text-[12px] text-slate-400 leading-relaxed">{v.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Cross-genre depth strip */}
      <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}} className="mt-6 grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl p-6 border backdrop-blur-xl" style={{ borderColor: `${V}44`, background: `${V}08` }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: V }}>// FICTION</div>
          <h4 className="font-display text-lg font-bold">Worldbuilding, reimagined.</h4>
          <p className="mt-2 text-[13px] text-slate-400">Rich atmospheric audio, character portraits, and interactive story nodes turn every chapter into an experience.</p>
        </div>
        <div className="rounded-2xl p-6 border backdrop-blur-xl" style={{ borderColor: `${A}44`, background: `${A}08` }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: A }}>// NON-FICTION</div>
          <h4 className="font-display text-lg font-bold">History, brought to life.</h4>
          <p className="mt-2 text-[13px] text-slate-400">Primary-source document popups, 3D site reconstructions, and chronological timeline overlays.</p>
        </div>
      </motion.div>
    </SlideFrame>
  );
}

function SlideAgeUX() {
  const ages = [
    { range: "3 – 7", label: "Early Readers", Icon: Baby, desc: "Big touch targets · phonics audio · picture glossaries · guided-tap reveal.", c: "#ec4899" },
    { range: "8 – 12", label: "Middle Grade", Icon: Users, desc: "Scholastic-style illustrated layouts · inline context · atmospheric audio.", c: A },
    { range: "13 – 17", label: "Young Adult", Icon: GraduationCap, desc: "Cinematic soundscapes · character portraits · interactive worldmaps.", c: V },
    { range: "18+", label: "Adult / General", Icon: Briefcase, desc: "Clean typography · primary-source popups · professional narration.", c: "#10b981" },
  ];
  return (
    <SlideFrame num={4} kicker="// 04 · AGE-ADAPTIVE UX & OPEN CATALOG">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Universal accessibility. <span style={{ color: V }}>One reader.</span> Four <span style={{ color: A }}>adaptive layers.</span></h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Device-agnostic HTML5 web app that works across phones, tablets, and laptops with zero app-store friction. <span style={{ color: V }} className="font-semibold">Unrestricted catalog access</span> — young readers can explore higher-grade subjects freely, and the UI dynamically adapts.</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {ages.map((a, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.6,delay:i*0.08}} className="relative rounded-2xl p-6 border-2 backdrop-blur-xl overflow-hidden" style={{ borderColor: `${a.c}55`, background: `linear-gradient(180deg, ${a.c}10, rgba(15,23,42,0.6))` }}>
            <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full blur-2xl opacity-40" style={{ background: a.c }} aria-hidden />
            <div className="relative">
              <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: `${a.c}1A`, border: `1px solid ${a.c}66` }}>
                <a.Icon size={18} style={{ color: a.c }} />
              </div>
              <div className="mt-4 font-display text-4xl font-bold tracking-tighter" style={{ color: a.c }}>{a.range}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{a.label}</div>
              <p className="mt-4 text-[12px] text-slate-300 leading-relaxed">{a.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl p-5 bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>// REAL-TIME STATE SWITCHING:</span>
          {["Font scale", "Vocabulary helpers", "Media depth", "Audio richness", "Interaction density"].map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ background: `${V}0F`, border: `1px solid ${V}44`, color: V }}>
              <Check size={9} /> {c}
            </span>
          ))}
        </div>
      </div>
    </SlideFrame>
  );
}

function SlideMVP() {
  const systems = [
    { Icon: BookOpen, title: "HTML5 Universal Reader", desc: "Fully responsive with age-tailored UI layers and VARK controls. Runs on any device without app-store install." },
    { Icon: Wand2, title: "Dream CapCha Author Studio", desc: "WYSIWYG rich-text editor with automated AI asset generation. Powered by OpenAI + ElevenLabs pipelines." },
    { Icon: Wallet, title: "Built-In Monetization Engine", desc: "Integrated Stripe checkout for pre-orders, digital sales, and creator subscriptions — live at MVP." },
  ];
  return (
    <SlideFrame num={5} kicker="// 05 · PRODUCT READINESS · THE 85% MVP">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Live and <span style={{ color: A }}>revenue-ready</span>. <span style={{ color: V }}>Not vaporware.</span></h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">A functional MVP is already live with the three core systems shipping. Capital efficiency: built for rapid deployment so we can onboard authors and generate revenue immediately — no more waiting for traditional long dev cycles.</p>

      {/* Huge progress bar */}
      <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}} className="mt-10 bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-8">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>// PLATFORM COMPLETION</div>
            <h3 className="mt-2 font-display text-3xl font-bold">85% functional MVP</h3>
          </div>
          <div className="text-right">
            <div className="font-display text-6xl lg:text-7xl font-bold tracking-tighter" style={{ color: V }}>85%</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-1">shipped · <span style={{color: A}}>15% remaining</span></div>
          </div>
        </div>
        <div className="h-5 rounded-full bg-slate-950 border border-slate-800 overflow-hidden relative">
          <motion.div initial={{width:0}} whileInView={{width:"85%"}} viewport={{once:true}} transition={{duration:1.6,ease:"easeOut"}} className="h-full rounded-full relative" style={{ background: `linear-gradient(90deg, ${V}, ${A})` }}>
            <div className="absolute inset-0 opacity-30 animate-pulse" style={{ background: `linear-gradient(90deg, transparent, ${V}88, transparent)` }} />
          </motion.div>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">
          <div>0%</div><div className="text-center">25%</div><div className="text-center">50%</div><div className="text-right">100%</div>
        </div>
      </motion.div>

      <div className="mt-6 grid md:grid-cols-3 gap-3">
        {systems.map((s, i) => (
          <motion.div key={i} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.55,delay:i*0.08}} className="rounded-2xl p-6 border-2 backdrop-blur-xl" style={{ borderColor: `${V}55`, background: `linear-gradient(135deg, ${V}10, rgba(15,23,42,0.6))` }}>
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${V}1A`, border: `1px solid ${V}66` }}>
                <s.Icon size={17} style={{ color: V }} />
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em] bg-emerald-500/10 border border-emerald-500/40 text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <h4 className="mt-4 font-display text-lg font-bold">{s.title}</h4>
            <p className="mt-2 text-[12px] text-slate-400 leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </SlideFrame>
  );
}

function SlideRevenue() {
  return (
    <SlideFrame num={6} kicker="// 06 · DUAL-ENGINE MONETIZATION">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Two revenue engines. <span style={{ color: V }}>Creator</span> value. <span style={{ color: A }}>Consumer</span> value.</h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">Monetization is built directly into the platform to capture value on both sides of the marketplace simultaneously.</p>

      {/* Dual engine diagram */}
      <div className="mt-10 grid lg:grid-cols-[1fr_auto_1fr] gap-4 items-center">
        {/* B2C */}
        <motion.div initial={{opacity:0,x:-20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.7}} className="rounded-3xl p-7 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${A}55`, background: `linear-gradient(135deg, ${A}15, rgba(15,23,42,0.7))` }}>
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl opacity-40" style={{ background: A }} aria-hidden />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `${A}1A`, border: `1px solid ${A}66` }}>
                <BookOpen size={20} style={{ color: A }} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: A }}>// ENGINE 01</div>
                <h3 className="font-display text-2xl font-bold">B2C Consumer</h3>
              </div>
            </div>
            <ul className="space-y-2.5 mt-4">
              {[
                { t: "Individual title sales", d: "Direct digital purchases with instant catalog access" },
                { t: "Pre-order campaigns", d: "Anticipation-driven launches with founder-tier bonuses" },
                { t: "Family access passes", d: "Household bundles across all 4 age tiers" },
              ].map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${A}1A`, border: `1px solid ${A}44` }}>
                    <Check size={11} style={{ color: A }} />
                  </div>
                  <div><div className="text-[13px] font-semibold text-slate-100">{r.t}</div><div className="text-[11px] text-slate-400">{r.d}</div></div>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Center hub */}
        <motion.div initial={{opacity:0,scale:0.8}} whileInView={{opacity:1,scale:1}} viewport={{once:true}} transition={{duration:0.9,delay:0.3}} className="hidden lg:flex flex-col items-center gap-3">
          <div className="h-16 w-16 rounded-full flex items-center justify-center backdrop-blur-xl" style={{ background: `linear-gradient(135deg, ${V}44, ${A}44)`, border: `2px solid ${V}88` }}>
            <Zap size={26} style={{ color: V }} />
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-center text-slate-400">Sleepy Owl<br/>Platform Core</div>
        </motion.div>

        {/* B2B */}
        <motion.div initial={{opacity:0,x:20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.7}} className="rounded-3xl p-7 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${V}55`, background: `linear-gradient(135deg, ${V}15, rgba(15,23,42,0.7))` }}>
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl opacity-40" style={{ background: V }} aria-hidden />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `${V}1A`, border: `1px solid ${V}66` }}>
                <Building2 size={20} style={{ color: V }} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>// ENGINE 02</div>
                <h3 className="font-display text-2xl font-bold">B2B / Creator</h3>
              </div>
            </div>
            <ul className="space-y-2.5 mt-4">
              {[
                { t: "Author Studio subscriptions", d: "Tiered SaaS pricing for AI-assisted story creation" },
                { t: "EdTech institutional licensing", d: "Schools & libraries deploying the VARK literacy model" },
                { t: "Multi-publisher integrations", d: "Enterprise catalog partnerships & white-label options" },
              ].map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${V}1A`, border: `1px solid ${V}44` }}>
                    <Check size={11} style={{ color: V }} />
                  </div>
                  <div><div className="text-[13px] font-semibold text-slate-100">{r.t}</div><div className="text-[11px] text-slate-400">{r.d}</div></div>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </SlideFrame>
  );
}

function SlideMarket() {
  return (
    <SlideFrame num={7} kicker="// 07 · MARKET OPPORTUNITY · GO-TO-MARKET">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Tapping into a <span style={{ color: A }}>$28B+ market</span> with a <span style={{ color: V }}>phased approach</span>.</h2>

      {/* TAM stat + phases */}
      <div className="mt-10 grid lg:grid-cols-[1fr_1.5fr] gap-4 items-stretch">
        <motion.div initial={{opacity:0,scale:0.9}} whileInView={{opacity:1,scale:1}} viewport={{once:true}} transition={{duration:0.8}} className="rounded-3xl p-8 border-2 relative overflow-hidden flex flex-col justify-center" style={{ borderColor: `${A}66`, background: `radial-gradient(circle at 30% 30%, ${A}20, rgba(15,23,42,0.7))` }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: A }}>// TOTAL ADDRESSABLE MARKET</div>
            <div className="mt-4 font-display text-6xl lg:text-8xl font-bold tracking-tighter" style={{ color: A }}>$28B+</div>
            <p className="mt-4 text-[13px] text-slate-300 leading-relaxed">Combined market across Digital Publishing, Children&apos;s Media, and EdTech Literacy verticals — the three categories Sleepy Owl serves natively.</p>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {["Digital Publishing", "Children's Media", "EdTech Literacy"].map((t) => (
                <span key={t} className="rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ background: `${A}0F`, border: `1px solid ${A}44`, color: A }}>{t}</span>
              ))}
            </div>
        </motion.div>

        <div className="grid gap-3">
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}} className="rounded-2xl p-6 border-2 backdrop-blur-xl" style={{ borderColor: `${V}55`, background: `linear-gradient(135deg, ${V}10, rgba(15,23,42,0.6))` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${V}1A`, border: `1px solid ${V}66` }}><Rocket size={16} style={{ color: V }} /></div>
              <div><div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>PHASE 01 · IMMEDIATE</div><div className="font-display text-lg font-bold">Live MVP → Founding Authors + Pilots</div></div>
            </div>
            <p className="text-[12px] text-slate-400 leading-relaxed">Onboard founding authors, run pre-order book campaigns, secure pilot B2B contracts with elementary schools and learning centers. Revenue starts <span style={{color: A}} className="font-semibold">Day 1 post-close</span>.</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="rounded-2xl p-6 border-2 backdrop-blur-xl" style={{ borderColor: `${A}55`, background: `linear-gradient(135deg, ${A}10, rgba(15,23,42,0.6))` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${A}1A`, border: `1px solid ${A}66` }}><Target size={16} style={{ color: A }} /></div>
              <div><div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: A }}>PHASE 02 · POST-FUNDING (FINAL 15%)</div><div className="font-display text-lg font-bold">Scale + Native Apps + Multi-Publisher</div></div>
            </div>
            <p className="text-[12px] text-slate-400 leading-relaxed">Scale catalog acquisition, launch native iOS/Android PWA apps, expand multi-publisher licensing integrations, and deepen the AI content pipeline.</p>
          </motion.div>
        </div>
      </div>
    </SlideFrame>
  );
}

function SlideAsk() {
  const alloc = [
    { pct: 40, label: "Marketing & Author Acquisition", desc: "Onboard 100+ launch authors · drive reader pre-orders", c: V },
    { pct: 35, label: "Product Polish · The Final 15%", desc: "Native app deployments · offline sync · AI pipeline", c: A },
    { pct: 25, label: "B2B Sales & Institutional Pilots", desc: "School district outreach · library systems · pilots", c: "#10b981" },
  ];
  // Build donut segments (conic-gradient)
  const g = `conic-gradient(${V} 0% 40%, ${A} 40% 75%, #10b981 75% 100%)`;
  return (
    <SlideFrame num={8} kicker="// 08 · THE ASK · CAPITAL ALLOCATION">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">Seeking <span style={{ color: V }}>seed / angel funding</span> — or <span style={{ color: A }}>strategic partnership</span>.</h2>
      <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">The platform is live, the pipes are built, the AI stack is wired. Capital is fuel — not runway to figure out what to build.</p>

      <div className="mt-10 grid lg:grid-cols-[1fr_1.5fr] gap-6 items-center">
        {/* Donut chart */}
        <motion.div initial={{opacity:0,scale:0.9}} whileInView={{opacity:1,scale:1}} viewport={{once:true}} transition={{duration:0.9}} className="flex flex-col items-center">
          <div className="relative w-72 h-72 rounded-full" style={{ background: g, boxShadow: `0 30px 60px -20px rgba(0,0,0,0.6)` }}>
            <div className="absolute inset-8 rounded-full bg-slate-950 flex flex-col items-center justify-center border-2 border-slate-800">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Use of funds</div>
              <div className="mt-1 font-display text-3xl font-bold" style={{ color: V }}>100%</div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">deployed on growth</div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            {alloc.map((a) => (
              <span key={a.label} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em]" style={{ background: `${a.c}0F`, border: `1px solid ${a.c}66`, color: a.c }}>
                <span className="h-2 w-2 rounded-full" style={{ background: a.c }} /> {a.pct}%
              </span>
            ))}
          </div>
        </motion.div>

        {/* Alloc breakdown */}
        <div className="space-y-3">
          {alloc.map((a, i) => (
            <motion.div key={i} initial={{opacity:0,x:20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.6,delay:i*0.08}} className="rounded-2xl p-5 border-2 backdrop-blur-xl flex items-center gap-5" style={{ borderColor: `${a.c}55`, background: `linear-gradient(90deg, ${a.c}10, rgba(15,23,42,0.6))` }}>
              <div className="flex flex-col items-center justify-center h-16 w-16 rounded-2xl shrink-0" style={{ background: `${a.c}1A`, border: `1px solid ${a.c}66` }}>
                <div className="font-display text-2xl font-bold" style={{ color: a.c }}>{a.pct}%</div>
              </div>
              <div>
                <h4 className="font-display text-lg font-bold">{a.label}</h4>
                <p className="mt-1 text-[12px] text-slate-400 leading-relaxed">{a.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Closing quote */}
      <motion.blockquote initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.8,delay:0.4}} className="mt-14 text-center relative">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-64 w-64 rounded-full blur-3xl opacity-30" style={{ background: V }} aria-hidden />
        </div>
        <div className="relative font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">// CLOSING</div>
        <p className="relative font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-3xl mx-auto">
          &ldquo;Everyone&apos;s story deserves to be told — and <span style={{ color: A }}>every reader deserves to experience it.</span>&rdquo;
        </p>
      </motion.blockquote>
    </SlideFrame>
  );
}

function SlidePanelsToLife() {
  const COMIC = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/kadentpy_images%20%287%29.jpg";
  const clips = [
    {
      label: "PANEL 01 · Punk woman riding train",
      src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/bl0cn7nt_Punk_woman_riding_train_202608081153.mp4",
    },
    {
      label: "PANEL 02 · Peeking around the carriage",
      src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/sujdcdgx_Punk_woman_peeking_around_train_202608081153.mp4",
    },
    {
      label: "PANEL 03 · Diving toward camera",
      src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/e3gfv71m_Woman_diving_towards_camera_202608081153.mp4",
    },
  ];

  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80 overflow-hidden" data-testid="owl-panels-to-life">
      <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
      <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${V}25` }} />
      <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${A}20` }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}}>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: A, background: `${A}0F`, border: `1px solid ${A}44` }}>
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: A, boxShadow: `0 0 10px ${A}` }} />
            // DEMO · DREAM CAPCHA · PANELS TO LIFE
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
            Still panels <span style={{ color: V }}>→</span> <span style={{ color: A }}>living scenes</span>.
          </h2>
          <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">
            One click inside the Author Studio and a static comic page becomes a fully animated sequence. Every source panel maps to a Veo-3 / Kling-generated video loop, embedded directly into the reader at the same word or scene-tag anchor.
          </p>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-[0.9fr_1.4fr] gap-6 items-start">
          {/* Source comic panel */}
          <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}} className="relative">
            <div className="absolute -inset-3 rounded-3xl blur-2xl opacity-60" style={{ background: `radial-gradient(circle at 30% 30%, ${V}44, transparent 70%)` }} aria-hidden />
            <div className="relative rounded-2xl overflow-hidden border-2 backdrop-blur-xl bg-slate-950" style={{ borderColor: `${V}55` }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800/80 bg-slate-900/70">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>// SOURCE PANEL · STILL</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">Comic · Ink + Color</div>
              </div>
              <img
                src={COMIC}
                alt="Source comic panel — punk woman on a train"
                loading="lazy"
                className="w-full block"
                data-testid="owl-panels-source"
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${V}15`, border: `1px solid ${V}55` }}>
                <BookOpen size={13} style={{ color: V }} />
              </div>
              <div className="text-[12px] text-slate-400 leading-tight">
                Uploaded by the author. <span style={{ color: V }} className="font-semibold">One click</span> → three video generations queued via the AI pipeline.
              </div>
            </div>
          </motion.div>

          {/* Generated videos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {clips.map((c, i) => (
              <motion.div
                key={i}
                initial={{opacity:0,y:24}}
                whileInView={{opacity:1,y:0}}
                viewport={{once:true,margin:"-80px"}}
                transition={{duration:0.65,delay:i*0.1}}
                className="relative"
                data-testid={`owl-panel-clip-${i}`}
              >
                <div className="absolute -inset-2 rounded-3xl blur-xl opacity-50" style={{ background: `radial-gradient(circle at 50% 30%, ${A}44, transparent 65%)` }} aria-hidden />
                <div className="relative rounded-2xl overflow-hidden border-2 backdrop-blur-xl bg-slate-950" style={{ borderColor: `${A}55` }}>
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800/80 bg-slate-900/70">
                    <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: A }}>
                      <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: A, boxShadow: `0 0 8px ${A}` }} />
                      LIVE · 0{i + 1}
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">AI Video</div>
                  </div>
                  <video
                    src={c.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full aspect-[9/16] object-cover bg-black"
                  />
                  <div className="px-3 py-2.5 border-t border-slate-800/80 bg-slate-900/40">
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: A }}>{c.label}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom tech pipeline strip */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.2}} className="mt-8 rounded-2xl p-5 bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>// PIPELINE:</span>
            {[
              { l: "Panel upload", Icon: BookOpen, c: V },
              { l: "Scene tagging", Icon: Wand2, c: V },
              { l: "Veo 3 / Kling render", Icon: Sparkles, c: A },
              { l: "Reader embed", Icon: Zap, c: "#10b981" },
            ].map((s, i, arr) => (
              <div key={i} className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em]" style={{ background: `${s.c}0F`, border: `1px solid ${s.c}55`, color: s.c }}>
                  <s.Icon size={10} /> {s.l}
                </span>
                {i < arr.length - 1 && <span className="font-mono text-[10px] text-slate-600">→</span>}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SlideFeaturedTitle() {
  const VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/svxbwsje_Anya_opening_eyes_with_sparks_202608110945.mp4";
  const tags = ["Fantasy", "Mythic", "Celestial", "Coming-of-Age"];
  const scenes = [
    { label: "Chapter 01 · The Awakening", detail: "Anya opens her eyes with celestial sparks" },
    { label: "Chapter 04 · The Protector's Line", detail: "Sarah's bloodline revealed · reality bends" },
    { label: "Chapter 09 · The Celestial Court", detail: "Metatron's grief confronts destiny" },
  ];
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80 overflow-hidden" data-testid="owl-featured-title">
      <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${V}25` }} />
      <div className="absolute -bottom-24 -left-24 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${A}20` }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}}>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: V, background: `${V}0F`, border: `1px solid ${V}44` }}>
            <BookOpen size={11} /> // FEATURED TITLE · LIVE ON THE PLATFORM
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
            One book. <span style={{ color: V }}>Cinematic scenes</span> on demand.
          </h2>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-6 items-start">
          {/* Video preview */}
          <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7}} className="relative" data-testid="owl-nephilim-video-wrap">
            <div className="absolute -inset-3 rounded-3xl blur-2xl opacity-70" style={{ background: `radial-gradient(circle at 30% 30%, ${V}44, transparent 60%), radial-gradient(circle at 70% 80%, ${A}33, transparent 60%)` }} aria-hidden />
            <div className="relative rounded-2xl overflow-hidden border-2 backdrop-blur-xl bg-slate-950 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.7)]" style={{ borderColor: `${V}55` }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800/80 bg-slate-900/70">
                <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: V }}>
                  <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: V, boxShadow: `0 0 10px ${V}` }} />
                  LIVE SCENE · CH.01
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">AI Video · Dream CapCha</div>
              </div>
              <video
                src={VIDEO}
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                className="w-full aspect-video object-cover bg-black"
                data-testid="owl-nephilim-video"
              />
              <div className="px-4 py-3 border-t border-slate-800/80 bg-slate-900/40 flex items-center justify-between">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: A }}>THE AWAKENING</div>
                  <div className="text-[12px] text-slate-300 mt-0.5">Anya opens her eyes with celestial sparks</div>
                </div>
                <Sparkles size={16} style={{ color: A }} />
              </div>
            </div>
          </motion.div>

          {/* Book synopsis card */}
          <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-80px"}} transition={{duration:0.7,delay:0.1}} className="rounded-2xl p-7 border-2 backdrop-blur-xl relative overflow-hidden" style={{ borderColor: `${V}55`, background: `linear-gradient(135deg, ${V}15, rgba(15,23,42,0.7))` }}>
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full blur-3xl opacity-40" style={{ background: V }} aria-hidden />
            <div className="relative">
              {/* Title block */}
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: A }}>
                <Sparkles size={11} /> ORIGINAL FICTION
              </div>
              <h3 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.98]" style={{ color: V }}>
                Nephilim
              </h3>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br flex items-center justify-center font-display text-sm font-bold text-slate-950" style={{ backgroundImage: `linear-gradient(135deg, ${V}, ${A})` }}>
                  L
                </div>
                <div className="text-[13px]">
                  <span className="font-mono uppercase tracking-[0.22em] text-slate-400 mr-1.5 text-[10px]">A story by</span>
                  <span className="font-display font-bold text-slate-100">Lao</span>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-5 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span key={t} className="rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ background: `${V}0F`, border: `1px solid ${V}55`, color: V }}>
                    {t}
                  </span>
                ))}
              </div>

              {/* Synopsis */}
              <div className="mt-6 pt-5 border-t border-slate-800/60">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: A }}>// SYNOPSIS</div>
                <p className="text-[13px] text-slate-300 leading-relaxed">
                  Born of an angel and a mortal, <span style={{ color: V }} className="font-semibold">Anya</span> grows up in the shadow of her father <span style={{ color: A }} className="font-semibold">Metatron&apos;s</span> grief over her late mother, Sarah. When Anya discovers Sarah was actually a descendant of an ancient bloodline of human <span className="font-semibold text-slate-100">Protectors</span>, she uncovers a legacy of intentional defiance against cosmic destiny. Blending raw celestial power with her mother&apos;s reality-bending protective abilities, Anya must embrace both sides of her heritage to stand against the celestial court and forge her own path.
                </p>
              </div>

              {/* Auto-generated scene list */}
              <div className="mt-6 pt-5 border-t border-slate-800/60">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: V }}>// AUTO-GENERATED SCENES</div>
                <ul className="space-y-2">
                  {scenes.map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 font-mono text-[9px] font-bold" style={{ background: `${V}1A`, border: `1px solid ${V}55`, color: V }}>
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: A }}>{s.label}</div>
                        <div className="text-[12px] text-slate-300 mt-0.5">{s.detail}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============ MAIN PAGE ============
export default function SleepyOwlPitch() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);

  useEffect(() => {
    document.title = "Sleepy Owl · Investor Vault";
    return () => { document.title = "TraceElements · We architect intelligent infrastructure."; };
  }, []);

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="owl-pitch-page">
      <SlideCover />
      <SlideProblem />
      <SlideSolution />
      <SlidePanelsToLife />
      <SlideFeaturedTitle />
      <SlideAgeUX />
      <SlideMVP />
      <SlideRevenue />
      <SlideMarket />
      <SlideAsk />

      {/* Footer strip */}
      <section className="py-8 bg-slate-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 pitch-no-print">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            // CONFIDENTIAL · Sleepy Owl + Dream CapCha · Prepared by TraceElements
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="owl-download-pdf"
              className="inline-flex items-center gap-2 text-white hover:opacity-90 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all shadow-[0_0_20px_-6px_rgba(124,58,237,0.6)]"
              style={{ background: V }}
            >
              <Download size={12} /> Download PDF
            </button>
            <button
              onClick={() => exportPitchPptx({ vaultName: "Sleepy Owl", title: "Sleepy Owl · Reimagining reading for learners", subtitle: "Multi-sensory interactive publishing platform — investor deck.", brand: "7C3AED", accent: "F59E0B", slides: OWL_PPTX_SLIDES })}
              data-testid="owl-download-pptx"
              className="inline-flex items-center gap-2 border px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all"
              style={{ borderColor: `${V}88`, background: `${V}1A`, color: V }}
            >
              <Download size={12} /> Download PPT
            </button>
            <button
              onClick={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.reload(); }}
              data-testid="owl-relock"
              className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] transition-all"
            >
              <Lock size={11} /> Relock Session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
